import { useMemo, useState } from "react";
import { adminApi, eventsApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { Badge, Card, CardSection, Field, Input, Modal, Button, Spinner, useToast } from "@/components/ui";
import type { Vocabulary } from "@/lib/types/config";
import type { BlueprintDetail } from "@/lib/types/api";
import { applyBlueprintConfig, planBlueprint, type BlueprintPlan } from "@/lib/utils/blueprint";
import { useBuilder } from "../BuilderContext";
import { IconBadgeCheck, IconSparkles } from "../components/icons";
import { cn } from "@/lib/utils/cn";

/**
 * What kind of experience is this?
 *
 * A blueprint decides the language and shape of the whole booth — a football
 * match asks guests to "Choose your club", a wedding asks whose side they're
 * on, a brand activation asks nothing at all because there are no sides. The
 * booth components are identical in every case; only this data differs.
 *
 * Every term is overridable per event, so an operator running something nobody
 * anticipated can rename the nouns without waiting for us to ship a blueprint.
 */

const OVERRIDABLE: Array<{ key: keyof Vocabulary; label: string; hint?: string }> = [
  { key: "participant_prompt", label: "Participant question", hint: "Shown at the top of the selection screen." },
  { key: "participant_hint", label: "Participant hint" },
  { key: "participant_singular", label: "Participant, singular", hint: "Club, Side, House, Team…" },
  { key: "participant_plural", label: "Participant, plural" },
  { key: "scene_prompt", label: "Scene question" },
  { key: "scene_hint", label: "Scene hint" },
  { key: "scene_singular", label: "Scene, singular", hint: "Vibe, Moment, Look, Backdrop…" },
  { key: "scene_plural", label: "Scene, plural" },
  { key: "capture_verb", label: "Capture button", hint: "“Take photo”, “Snap it”, “Lock it in”…" },
  { key: "result_title", label: "Result headline" },
];

export function ExperienceTab() {
  const { draft, update, event, setEvent } = useBuilder();
  const toast = useToast();
  const [pending, setPending] = useState<{ blueprint: BlueprintDetail; plan: BlueprintPlan } | null>(null);
  const [applying, setApplying] = useState(false);
  const blueprints = useAsync(() => adminApi.blueprints.list(), []);

  const selectedSlug = draft.experience?.blueprint_slug ?? event.blueprint_slug ?? "";
  const overrides = draft.experience?.taxonomy ?? {};
  const selected = blueprints.data?.find((item) => item.slug === selectedSlug);

  /**
   * Choosing a format now reconfigures the booth, not just its wording.
   *
   * Nothing happens on the click itself. Switching format can turn off a mode
   * the operator relies on or re-add a screen they deliberately removed, so
   * they get told exactly what will change and it lands only if they accept.
   */
  async function chooseBlueprint(id: number, slug: string) {
    if (slug === selectedSlug) return;

    try {
      // The list endpoint omits `defaults` — it's the detail payload that
      // carries what the booth should become.
      const blueprint = await adminApi.blueprints.get(id);
      setPending({ blueprint, plan: planBlueprint(blueprint, event, draft) });
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "Could not load that format");
    }
  }

  async function applyPending() {
    if (!pending) return;
    setApplying(true);

    try {
      // The event record first. If this fails, the config is untouched and the
      // booth is still coherent — the reverse order could leave a config
      // expecting a mode that never got switched on.
      if (Object.keys(pending.plan.record).length > 0) {
        setEvent(await eventsApi.update(event.id, pending.plan.record));
      }

      update((current) => applyBlueprintConfig(current, pending.blueprint, pending.plan));
      toast.success(`Booth set up for ${pending.blueprint.name}`);
      setPending(null);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "Could not apply that format");
    } finally {
      setApplying(false);
    }
  }

  function setOverride(key: string, value: string) {
    update((current) => {
      const next = { ...(current.experience?.taxonomy ?? {}) };
      if (value.trim()) next[key] = value;
      else delete next[key];
      return { ...current, experience: { ...current.experience, taxonomy: next } };
    });
  }

  // What the booth will actually say, once overrides are layered on.
  const preview = useMemo(() => resolvePreview(overrides), [overrides]);

  return (
    <div className="flex flex-col gap-8">
      {pending && (
        <Modal
          open
          onClose={() => setPending(null)}
          title={`Set this booth up as “${pending.blueprint.name}”?`}
          description="Choosing a format reconfigures the booth, not just its wording. Here's exactly what changes."
        >
          <ul className="mb-5 flex flex-col gap-2">
            {pending.plan.changes.map((line) => (
              <li key={line} className="flex items-start gap-2 text-sm text-haze-200">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent" />
                {line}
              </li>
            ))}
          </ul>

          <p className="mb-5 rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-2 text-xs leading-relaxed text-haze-400">
            Your vibes, acts, branding and prices are left alone. Any wording you
            overrode by hand is reset to this format's language.
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            <Button variant="primary" loading={applying} onClick={() => void applyPending()}>
              Apply {pending.blueprint.name}
            </Button>
          </div>
        </Modal>
      )}

      <CardSection
        eyebrow="Format"
        title="Experience blueprint"
        description="Pick the closest format. It sets the language, the flow, and what extra details guests are asked for — all of which you can override below."
      >
        {blueprints.loading ? (
          <div className="grid place-items-center py-10"><Spinner className="text-accent" /></div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(blueprints.data ?? []).map((blueprint) => {
              const active = blueprint.slug === selectedSlug;
              return (
                <button
                  key={blueprint.id}
                  onClick={() => void chooseBlueprint(blueprint.id, blueprint.slug)}
                  className={cn(
                    "flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors",
                    active ? "border-accent/60 bg-accent/[0.06]" : "border-ink-600 bg-ink-750 hover:border-ink-500",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={cn("grid h-8 w-8 place-items-center rounded-lg",
                      active ? "bg-accent text-accent-ink" : "bg-ink-700 text-haze-300")}>
                      {blueprint.slug.includes("football") || blueprint.slug.includes("esports")
                        ? <IconBadgeCheck size={15} />
                        : <IconSparkles size={15} />}
                    </span>
                    {blueprint.system ? <Badge tone="neutral">Library</Badge> : <Badge tone="aqua">Custom</Badge>}
                  </div>
                  <span className="text-sm font-semibold text-haze-100">{blueprint.name}</span>
                  {blueprint.description && (
                    <span className="text-xs leading-relaxed text-haze-400">{blueprint.description}</span>
                  )}
                  <span className="mt-1 flex flex-wrap gap-1">
                    {blueprint.capture_modes.map((mode) => (
                      <span key={mode} className="rounded bg-ink-800 px-1.5 py-0.5 text-2xs text-haze-400">
                        {mode.replace(/_/g, " ")}
                      </span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {!selectedSlug && (
          <p className="rounded-lg border border-warn/40 bg-warn/5 px-3 py-2 text-xs leading-relaxed text-warn">
            No blueprint selected — the booth will use neutral wording. That's fine, but picking one
            gives you sensible defaults to start from.
          </p>
        )}
      </CardSection>

      <CardSection
        eyebrow="Language"
        title="What the booth says"
        description="Leave a field blank to inherit it from the blueprint. Fill one in when your event calls things something else."
      >
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          {OVERRIDABLE.map((entry) => (
            <Field key={String(entry.key)} label={entry.label} hint={entry.hint}>
              <Input
                placeholder={preview[String(entry.key)] ?? ""}
                value={(overrides[String(entry.key)] as string) ?? ""}
                onChange={(e) => setOverride(String(entry.key), e.target.value)}
              />
            </Field>
          ))}
        </Card>
      </CardSection>

      {selected && (
        <CardSection
          eyebrow="Guest input"
          title="Extra details this format collects"
          description="Defined by the blueprint. These become available as prompt variables, so a football booth can put a jersey number on the shirt."
        >
          <Card className="flex flex-col gap-3 p-5">
            <BlueprintFields blueprintId={selected.id} />
          </Card>
        </CardSection>
      )}
    </div>
  );
}

function BlueprintFields({ blueprintId }: { blueprintId: number }) {
  const detail = useAsync(() => adminApi.blueprints.get(blueprintId), [blueprintId]);

  if (detail.loading) return <Spinner className="text-accent" />;
  const fields = detail.data?.custom_fields ?? [];

  if (fields.length === 0) {
    return <p className="text-sm text-haze-400">This format doesn't ask for anything extra.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {fields.map((field) => (
        <li key={field.key} className="flex flex-wrap items-center gap-3 rounded-lg bg-ink-800 px-3 py-2.5">
          <span className="text-sm text-haze-100">{field.label}</span>
          <Badge tone="neutral">{field.type}</Badge>
          {field.required && <Badge tone="warn">Required</Badge>}
          <code className="ml-auto rounded bg-ink-900 px-1.5 py-0.5 text-2xs text-accent">
            {field.prompt_token}
          </code>
        </li>
      ))}
    </ul>
  );
}

/** Neutral fallbacks, matching EventBlueprint::DEFAULT_TAXONOMY on the server. */
function resolvePreview(overrides: Vocabulary): Record<string, string> {
  const defaults: Record<string, string> = {
    participant_singular: "Participant",
    participant_plural: "Participants",
    participant_prompt: "Choose your side",
    participant_hint: "Pick who you're here for",
    scene_singular: "Vibe",
    scene_plural: "Vibes",
    scene_prompt: "Choose your vibe",
    scene_hint: "Select a scene theme for your photo",
    capture_verb: "Take photo",
    result_title: "Here's your photo",
  };

  Object.entries(overrides).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim()) defaults[key] = value;
  });

  return defaults;
}
